const STORAGE_KEY = 'mockery_bundle_url';
const DEBUG_KEY = 'mockery_debug';

// Check if Mockery is disabled via environment variables
const isMockeryDisabled = (): boolean => {
  try {
    // Check for Vite environment variable
    if (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_MOCKERY_DISABLED) {
      return (import.meta as any).env.VITE_MOCKERY_DISABLED === 'true';
    }
    
    // Check for Create React App environment variable
    if (typeof window !== 'undefined' && (window as any).process?.env?.REACT_APP_MOCKERY_DISABLED) {
      return (window as any).process.env.REACT_APP_MOCKERY_DISABLED === 'true';
    }
    
    // Check for general environment variable (Node.js)
    if (typeof globalThis !== 'undefined' && (globalThis as any).process?.env?.MOCKERY_DISABLED) {
      return (globalThis as any).process.env.MOCKERY_DISABLED === 'true';
    }
    
    return false;
  } catch (error) {
    // If any environment check fails, default to enabled
    return false;
  }
};

// Log disabled state on module load
if (isMockeryDisabled()) {
  console.log('[Mockery] SDK disabled via environment variable');
}

/**
 * Set a custom bundle URL to be loaded instead of the default bundle.
 * Call this when a user logs in or switches tenants.
 * 
 * @param bundleUrl - The URL of the custom bundle, or undefined to clear
 * @param reload - Whether to reload the page immediately (default: true)
 */
export function setCustomBundle(bundleUrl: string | undefined, reload = true): void {
  if (isMockeryDisabled()) {
    console.log('[Mockery] setCustomBundle disabled');
    return;
  }
  
  try {
    bundleUrl = bundleUrl || undefined;
    const currentBundle = getCustomBundle();
    
    // Exit early if bundle URL is unchanged
    if (bundleUrl === currentBundle) {
      return;
    }
    
    if (bundleUrl) {
      localStorage.setItem(STORAGE_KEY, bundleUrl);
      console.log('[Mockery] Custom bundle set:', bundleUrl);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      console.log('[Mockery] Custom bundle cleared');
    }
    
    if (reload) {
      window.location.reload();
    }
  } catch (error) {
    console.error('[Mockery] Failed to set custom bundle:', error);
  }
}

/**
 * Get the currently set custom bundle URL
 */
export function getCustomBundle(): string | undefined {
  if (isMockeryDisabled()) {
    return undefined;
  }
  
  try {
    return localStorage.getItem(STORAGE_KEY) || undefined;
  } catch (error) {
    console.warn('[Mockery] Failed to get custom bundle:', error);
    return undefined;
  }
}

/**
 * Clear the custom bundle URL and optionally reload
 */
export function clearCustomBundle(reload = true): void {
  if (isMockeryDisabled()) {
    console.log('[Mockery] clearCustomBundle disabled');
    return;
  }
  
  setCustomBundle(undefined, reload);
}

/**
 * Set a custom bundle URL and reload the page
 */
export function reloadWithBundle(bundleUrl: string | undefined): void {
  if (isMockeryDisabled()) {
    console.log('[Mockery] reloadWithBundle disabled');
    return;
  }
  
  setCustomBundle(bundleUrl, true);
}

/**
 * Enable or disable debug logging
 */
export function setDebugMode(enabled: boolean): void {
  if (isMockeryDisabled()) {
    console.log('[Mockery] setDebugMode disabled');
    return;
  }
  
  try {
    if (enabled) {
      localStorage.setItem(DEBUG_KEY, 'true');
      console.log('[Mockery] Debug mode enabled');
    } else {
      localStorage.removeItem(DEBUG_KEY);
      console.log('[Mockery] Debug mode disabled');
    }
  } catch (error) {
    console.warn('[Mockery] Failed to set debug mode:', error);
  }
} 